import { useReducer } from 'react';
import { useApplication } from '@/components/application';
import { useMenu } from '@/components/shared';
import { FlatStructure } from '@/components/structure/FlatStructure';
import { GraphStructure } from '@/components/structure/GraphStructure';
import { OnboardingStructureCard } from '@/components/structure/OnboardingStructureCard';
import { useDefinitions } from '@/core/providers';
import { useApplicationStructureParams } from '@/routes/endClientArea/params';

type ConnectionViewType = 'FLAT' | 'GRAPH';

const useConnectionViewType = (): ConnectionViewType => {
	const excludedConnections = ['ACCOUNT_HOLDER'];
	const { applicationConnectionKind } = useApplicationStructureParams();
	const { defs } = useDefinitions();
	const application = useApplication();

	const entityDef = defs.entities[application.account.entityKind];
	const connectionDef = entityDef?.possibleConnections.find((c) => c.kind === applicationConnectionKind);
	const availableEntities = connectionDef?.fromEntities.map((e) => e.kind) ?? [];

	const filteredConnections = availableEntities.map((entityKind) => {
		const entity = defs.entities[entityKind];
		return entity?.possibleConnections.filter((connection) => !excludedConnections.includes(connection.kind)) ?? [];
	});

	const emptyConnectionsCount = filteredConnections.filter((connections) => !connections.length).length;

	if (emptyConnectionsCount === availableEntities.length) {
		return 'FLAT';
	}

	return 'GRAPH';
};

export const ApplicationConnectionRoute = () => {
	const { applicationConnectionKind } = useApplicationStructureParams();
	const connectionViewType = useConnectionViewType();
	const { menuItems } = useMenu();

	const formIndex = menuItems.findIndex((item) => item.isSelected);
	const currentMenuItem = menuItems.find((item) => item.isSelected);

	const localStorageKey = `structure-onboarding-screen-${applicationConnectionKind}`;
	const hasViewedStructureOnboarding = JSON.parse(localStorage.getItem(localStorageKey) ?? 'false');
	const [_, forceUpdate] = useReducer((x) => x + 1, 0);

	if (!hasViewedStructureOnboarding) {
		return (
			<OnboardingStructureCard
				connectionKind={applicationConnectionKind}
				label={currentMenuItem?.label ?? ''}
				structureType={connectionViewType}
				onSubmit={() => {
					localStorage.setItem(localStorageKey, 'true');
					forceUpdate();
				}}
				onCancel={() => {
					const nextForm = menuItems[formIndex + 1];
					nextForm.onClick();
				}}
			/>
		);
	}

	if (connectionViewType === 'FLAT') {
		return <FlatStructure />;
	}

	return <GraphStructure />;
};
